/**
 * Backblaze B2 pricing, shared by the desktop wizard's cost estimate, the
 * portal's Providers pricing table and the Backblaze guide's calculator, so
 * no two screens can quote different numbers for the same amount of data.
 */

/** Backblaze bills storage by the gigabyte, 10^9 bytes. */
const BILLING_GB_BYTES = 1_000_000_000;

/** Dollars per terabyte-month of stored data beyond the free allowance. */
export const BACKBLAZE_PRICE_PER_TB_MONTH = 6.95;

/** Gigabytes stored free of charge each month. */
export const BACKBLAZE_FREE_GB = 10;

/** When `BACKBLAZE_PRICE_PER_TB_MONTH` was last checked against Backblaze's
 * published pricing. */
export const BACKBLAZE_PRICE_CHECKED = "September 2026";

/**
 * What a backup costs per month. Three cases, not two: a backup can be
 * billable and still round to nothing at the ten cents we quote to, and
 * calling that one "free" would tell somebody storing 17 GB that they fit
 * inside a 10 GB allowance.
 */
export type BackblazeCostEstimate =
  /** Fits inside the free allowance, so it genuinely costs nothing. */
  | { status: "free" }
  /** Billable, but the monthly cost rounds below ten cents. */
  | { status: "under-ten-cents" }
  /** Billable, at `dollarsPerMonth` rounded to the nearest ten cents. */
  | { status: "priced"; dollarsPerMonth: number };

/**
 * What a backup of `bytes` would cost per month on Backblaze B2, from the
 * raw size — never from a headroom figure inflated for "room to grow".
 */
export function estimateBackblazeMonthlyCost(bytes: number): BackblazeCostEstimate {
  const billableGb = bytes / BILLING_GB_BYTES - BACKBLAZE_FREE_GB;
  if (billableGb <= 0) return { status: "free" };

  const dollarsPerMonth = Math.round(billableGb * (BACKBLAZE_PRICE_PER_TB_MONTH / 1000) * 10) / 10;
  return dollarsPerMonth <= 0 ? { status: "under-ten-cents" } : { status: "priced", dollarsPerMonth };
}

/**
 * The same estimate, from a gigabyte figure rather than raw bytes — what the
 * portal's calculator slider hands in. A thin wrapper rather than exporting
 * `BILLING_GB_BYTES` itself: the gigabyte-to-byte conversion stays in one
 * place, so a caller can't do that multiplication slightly wrong.
 */
export function estimateBackblazeMonthlyCostForGb(gigabytes: number): BackblazeCostEstimate {
  return estimateBackblazeMonthlyCost(gigabytes * BILLING_GB_BYTES);
}

/**
 * How we say a cost out loud, as a lower-case fragment a caller frames for
 * itself — the wizard trails a clause off it, the guide's calculator makes a
 * sentence of it.
 *
 * Here rather than at either call site because both screens were switching on
 * the same three cases in the same order, in two different apps, to say the
 * same three things. A fourth case would have needed both edited, and a
 * reworded one would have silently drifted them apart.
 */
export function backblazeCostPhrase(estimate: BackblazeCostEstimate): string {
  switch (estimate.status) {
    case "free":
      return `free — it fits in the free ${BACKBLAZE_FREE_GB} GB a month`;
    case "under-ten-cents":
      return "under 10¢ a month";
    case "priced":
      return `about $${estimate.dollarsPerMonth.toFixed(2)} a month`;
  }
}
